import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useTranslationWithFallback from 'hooks/use-translation-with-fallback';

// Store
import { useSelector } from 'react-redux';
import { selectCurrentRestaurant } from 'store/slices/restaurantsSlice';

// Utils
import bemify from 'utils/bemify';
import formatPrice from 'utils/format-price';

// Hooks
import useAllergens from 'hooks/use-allergens';

// Components
import Button from 'components/button';
import Icon from 'components/icon';
import Image from 'components/image';
import Modal from 'components/modal';
import QuantitySelector from 'components/quantity-selector';
import Tooltip from 'components/tooltip';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const ProductModal = ({
  allergensTranslations = null,
  className = null,
  onAddToCart = () => {},
  onClose = () => {},
  productDetails = null,
  restaurantCurrency = null,
  selectedLocale = null,
  show = false,
  translations = null
}) => {
  const { t, lang } = useTranslationWithFallback('common');

  const allAllergens = useAllergens(allergensTranslations);

  // State
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  // Selectors
  const currentRestaurant = useSelector(selectCurrentRestaurant);

  // Computed
  const entityId = productDetails?.entityId;
  const hasAllergens = Array.isArray(productDetails?.allergens) && !!productDetails.allergens.length;
  const imageUrl = productDetails?.imageUrl;
  const price = productDetails?.price;
  const productTranslations = productDetails?.translations;

  const title = (
    productTranslations?.[selectedLocale || lang]?.title ||
    productDetails?.fallbackTranslations?.title
  );

  const description = (
    productTranslations?.[selectedLocale || lang]?.description ||
    productDetails?.fallbackTranslations?.description
  );

  const ingredients = (
    productTranslations?.[selectedLocale || lang]?.ingredients ||
    productDetails?.fallbackTranslations?.ingredients
  );

  // Methods
  const handleAddToCartButtonClick = () => {
    onAddToCart(selectedQuantity);

    setSelectedQuantity(1);

    onClose();
  };

  const handleCloseButtonKeyDown = (event) => {
    if (event.key === 'Enter') onClose();
  };

  // Effects
  useEffect(() => {
    setSelectedQuantity(1);
  }, [show]);

  const modalHeader = (
    <div className={styles.productModal__header}>
      {!!imageUrl && (
        <>
          <div className={styles.productModal__imageWrapper}>
            <Image
              alt={title}
              layout="fill"
              objectFit="cover"
              objectPosition="center"
              sizes="(max-width: 575px) calc(100vw - 24px), 407px"
              src={imageUrl}
            />
          </div>
          <div
            className={styles.productModal__closeButton}
            role="button"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={handleCloseButtonKeyDown}
          >
            <Icon name="close" />
          </div>
        </>
      )}
      <h6 className={imageUrl ? styles.productModal__title_withMarginTop : styles.productModal__title_withMarginRight}>
        {title}
      </h6>
      {description && (
        <div className={styles.productModal__description}>
          {description}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      ariaTitle={title}
      className={`${styles.productModal}${className ? ` ${className}` : ''}`}
      customHeader={modalHeader}
      mobilePosition="bottom"
      noCloseIcon={!!imageUrl}
      show={show}
      size="s"
      onClose={onClose}
    >
      <>
        <div className={styles.productModal__priceAndAllergens}>
          <div className={styles.productModal__price}>
            {formatPrice(price, currentRestaurant?.currency || restaurantCurrency)}
          </div>
          {hasAllergens && (
            <div className={styles.productModal__allergensWrapper}>
              {productDetails.allergens.map((entry, index) => {
                const productAllergenDetails = allAllergens.find(allergen => allergen.id === entry);

                if (!productAllergenDetails) return null;

                return (
                  <Tooltip
                    key={index}
                    text={productAllergenDetails.label}
                    trigger={(
                      <div>
                        <Icon
                          className={styles.productModal__allergenIcon}
                          name={productAllergenDetails.icon}
                        />
                      </div>
                    )}
                  />
                );
              })}
            </div>
          )}
        </div>
        {!!ingredients && (
          <>
            <hr className={styles.productModal__dividerLine} />
            <div className={styles.productModal__ingredientsTitle}>
              {translations?.products?.ingredients || t('product_card_modal.ingredients')}
            </div>
            <div className={`${styles.productModal__ingredients} ${styles.productModal__ingredients_noMarginTop}`}>
              {ingredients}
            </div>
          </>
        )}
        {!!onAddToCart && (
          <footer className={styles.productModal__footer}>
            <QuantitySelector
              id={`quantity-selector-${entityId}`}
              value={selectedQuantity}
              onChange={setSelectedQuantity}
            />
            <Button
              size="s"
              text={translations?.actions?.addToCart || t('actions.add_to_cart')}
              theme="primary"
              onClick={handleAddToCartButtonClick}
            />
          </footer>
        )}
      </>
    </Modal>
  );
};

ProductModal.propTypes = {
  allergensTranslations: PropTypes.objectOf(PropTypes.string),
  className: PropTypes.string,
  onAddToCart: PropTypes.func,
  onClose: PropTypes.func,
  productDetails: PropTypes.shape({
    allergens: PropTypes.arrayOf(PropTypes.string),
    description: PropTypes.string,
    imageUrl: PropTypes.string,
    ingredients: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    translations: PropTypes.shape({}).isRequired
  }),
  restaurantCurrency: PropTypes.string,
  selectedLocale: PropTypes.string,
  show: PropTypes.bool,
  translations: PropTypes.shape({
    ingredients: PropTypes.string,
    products: PropTypes.shape({
      ingredients: PropTypes.string
    })
  })
};

export default ProductModal;
